"use client";
import { FormControl, FormControlProps, FormLabel } from "@chakra-ui/react";
import { Select, useChakraSelectProps } from "chakra-react-select";
import { useMemo } from "react";
import { GroupedCheckboxSelect } from "./GroupedCheckboxSelect";

export interface SelectOption<Value> {
  label: string;
  value: Value;
  /**
   * Greys the option out and blocks selection, leaving it visible — react-select's default
   * `isOptionDisabled` reads exactly this flag. Use it when an option is unavailable *because of
   * something else on the form*, so the reason stays legible; drop the option from the list instead
   * when it simply doesn't exist for the current context.
   */
  isDisabled?: boolean;
}

export interface SelectGroupOption<Value> {
  label: string;
  options: SelectOption<Value>[];
}

export type ChakraSelectFieldProps = ReturnType<typeof useChakraSelectProps>;
export type SelectFieldOptions<Value> =
  | SelectOption<Value>[]
  | SelectGroupOption<Value>[];

export interface SelectFieldProps<Value>
  extends Omit<ChakraSelectFieldProps, "onChange">,
    Pick<
      FormControlProps,
      "isDisabled" | "isInvalid" | "isReadOnly" | "isRequired"
    > {
  options: SelectFieldOptions<Value>;
  label?: string;
  selectType?: "checkbox-group" | "normal";
  onChange?: (value: SelectOption<Value>) => void;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const defaultSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    borderRadius: "9px",
    borderColor: state.isFocused ? "#3A4BD8" : "#DDE1E6",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(58,75,216,0.12)" : "none",
    fontSize: "13.5px",
    minHeight: "40px",
    "&:hover": { borderColor: state.isFocused ? "#3A4BD8" : "#C7CCD4" },
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: "10px",
    boxShadow: "0 12px 28px rgba(16,22,35,0.13)",
    padding: "5px",
    border: "1px solid #E5E8EC",
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    borderRadius: "7px",
    fontSize: "13px",
    padding: "9px 13px",
    backgroundColor: state.isSelected
      ? "#3A4BD8"
      : state.isFocused
        ? "#F0F1FE"
        : "transparent",
    color: state.isSelected ? "#fff" : "#3A4150",
    cursor: "pointer",
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: "#9AA2AE",
    fontSize: "13.5px",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "#14181F",
    fontSize: "13.5px",
  }),
};

export const getSelectFieldVariant = (variant: "checkbox-group" | "normal") => {
  switch (variant) {
    case "checkbox-group":
      return GroupedCheckboxSelect;
    default:
      return Select;
  }
};

export function SelectField<Value>({
  options,
  isDisabled,
  isInvalid,
  isReadOnly,
  isRequired,
  label,
  onChange,
  selectType,
  ...props
}: SelectFieldProps<Value>) {
  const Field = useMemo(
    (): React.ElementType => getSelectFieldVariant(selectType || "normal"),
    [selectType],
  );

  return (
    <FormControl
      {...{
        isDisabled,
        isInvalid,
        isReadOnly,
        isRequired,
      }}
    >
      {label && <FormLabel>{label}</FormLabel>}
      <Field
        useBasicStyles
        name="colors"
        colorScheme="primary"
        focusBorderColor="#3A4BD8"
        selectedOptionColorScheme="primary"
        placeholder="Select Option..."
        closeMenuOnSelect={true}
        size="md"
        styles={defaultSelectStyles}
        {...props}
        onChange={(value: unknown) => {
          onChange?.(value as SelectOption<Value>);
        }}
        options={options}
        autoComplete="off"
      />
    </FormControl>
  );
}
